import React, { FunctionComponent, ReactNode, useState, useCallback } from 'react';
import { ScrollProvider, IPosition, IScrollProviderProps as IDeps } from 'app/components/scroll/ScrollContext';

interface IProps {
  children: (deps: IDeps, ref: (element: Element | null) => void) => ReactNode;
}

export const ElementScrollProviderBase: FunctionComponent<IProps> = ({ children }) => {
  // Intentionally using useState instead of useRef for the scroller element,
  // because we want to create new versions of the scroll methods when the
  // element changes, so consumers of the scroll methods know that they might
  // need to re-trigger scrolling.
  //
  // In particular, this is needed if a child of <ElementScrollProvider> needs
  // to call `scrollTo()` or some other scroll method on mount: the child
  // component's useEffect callback will be triggered before this component's
  // callback ref (`setRef`) is called, meaning nothing will happen when the
  // child component tries to scroll. By making the value of `scrollTo`/etc.
  // change when the scroll element changes, we can cause the child element to
  // re-render after we have access to the `scroller` element and can actually
  // scroll.
  const [scroller, setScroller] = useState<Element | undefined>(undefined);
  const setRef = useCallback((node: Element | null) => {
    setScroller(node || undefined);
  }, []);

  const scrollTo = useCallback(({ x, y }: Partial<IPosition>) => {
    if (scroller) {
      if (x !== undefined) scroller.scrollLeft = x;
      if (y !== undefined) scroller.scrollTop = y;
    }
  }, [scroller]);

  const getPosition = useCallback(() => (
    scroller
    ? {
      x: scroller.scrollLeft,
      y: scroller.scrollTop,
    }
    : {
      x: 0,
      y: 0,
    }
  ), [scroller]);

  const getBounds = useCallback(() => {
    // Destruct DOMRect becacuse normally its fields are not
    // enumerable. TypeScript treats all fiels as enumerable so it
    // will produce false positives when using spread operator
    const { top, left, right, bottom, width, height } = scroller
      ? scroller.getBoundingClientRect()
      : { top: 0, left: 0, right: 0, bottom: 0, width: 0, height: 0 };

    return { top, left, right, bottom, width, height };
  }, [scroller]);

  const getScrollSize = useCallback(() => (
    scroller
    ? {
      scrollWidth: scroller.scrollWidth,
      scrollHeight: scroller.scrollHeight,
    }
    : {
      scrollWidth: 0,
      scrollHeight: 0,
    }
  ), [scroller]);

  const findElementByAnchor = useCallback((anchor: string): Element | null => (
    !scroller ? null :
    scroller.getAttribute('id') === anchor ? scroller :
    scroller.querySelector(`#${anchor}`)
  ), [scroller]);

  return (
    <React.Fragment>
      {children(
        {
          scrollEmitter: scroller,
          scrollTo,
          getPosition,
          getBounds,
          getScrollSize,
          findElementByAnchor,
        },
        setRef,
      )}
    </React.Fragment>
  );
};

interface IProviderProps {
  children: (ref: (element: Element | null) => void) => ReactNode;
  scrollId?: string;
}

const ElementScrollProvider: FunctionComponent<IProviderProps> = ({ scrollId, children }) =>
  <ElementScrollProviderBase>{(deps, setRef) =>
    <ScrollProvider
      {...deps}
      scrollId={scrollId}
    >
      {children(setRef)}
    </ScrollProvider>
  }</ElementScrollProviderBase>;

export default ElementScrollProvider;
